<!-- TEMPLATE -->
<template>
  <div class="wrapper">
    <dean-dialog
      title="Bestelregels importeren"
      :large="false"
      :addClose="true"
      @close="close"
    >
      <slot name="load">
      </slot>

      <dean-notification :is-modal="true">
      </dean-notification>

      <fade-transition group>
        <div class="dean-form-row" key="importFile">
          <div class="row">
            <div class="small-6 medium-6 large-6 hd-6 columns">
              <p class="input-label">importeer bestand <span style="color:red">*</span></p>
            </div>

            <div class="small-6 medium-6 large-6 hd-6 columns">
              <dean-input-file id="importFile" label="filename"
                               @file-changed="importFileChanged"
                               :requiredField="true">
              </dean-input-file>
            </div>
          </div>
        </div>

        <dean-select-form-row
          v-if="agreementOptions.length > 1"
          key="agreementSelect"
          placeholder="Kies een overeenkomst"
          label="Overeenkomst"
          :options="agreementOptions"
          :value="currentlySelectedAgreement()"
          id="partnerAgreement"
          :requiredField="true"
          :clearable="true"
          @input="selectedAgreementId = $event.value"
        ></dean-select-form-row>

        <div class="dean-form-row-errors" v-if="errors.length" key="errors">
            <div class="alert alert-danger" role="alert">
              <ol class="mb-0 pl-3">
                <li v-for="(error, index) in errors" :key="index">
                  {{ error.message }}
                </li>
              </ol>
          </div>
        </div>
      </fade-transition>

      <span slot="dean-dialog-button-group">
        <dean-button :danger="true" :dialog-button="true" @click.native="close()">Annuleren</dean-button>
        <dean-button :success="true" :dialog-button="true" @click.native="save()">Versturen</dean-button>
      </span>
    </dean-dialog>
  </div>
</template>

<!-- SCRIPT -->
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop} from 'vue-property-decorator';
import axios from 'axios';
import {
    // SVG
    warningIcon,
    // COMPONENTS
    deanDialog,
    deanDialogButtonGroup,
    deanInputFile
  } from '@deanone/dean-components';
import deanButton from '@/components/deanComponents/deanButton.vue';
import DeanNotification from '@/components/deanComponents/deanNotification.vue';
import DeanSelectFormRow from '@/components/deanComponents/deanSelectFormRow.vue';
import {deanDialogBus} from '@/main';
import {validationMixin} from 'vuelidate';
import {required, requiredIf} from 'vuelidate/lib/validators';
import {Validations} from 'vuelidate-property-decorators';
import {createNotification} from '@/mixins/validations';
import {FadeTransition} from 'vue2-transitions';
import {xlsx} from '@/mixins/validators';

@Component({
    components: {
      warningIcon,
      DeanNotification,
      deanDialog,
      deanButton,
      deanInputFile,
      deanDialogButtonGroup,
      DeanSelectFormRow,
      FadeTransition,
    },
    mixins: [validationMixin],
  })

  export default class OrderImportOrderlinesDialog extends Vue {
    @Validations()
    get validations() {
      const valData: any = {
        data: {
          importFile: {
            required,
            xlsx,
          },
        },
        selectedAgreementId: {
          required: requiredIf(() => this.agreementOptions.length > 1),
        },
      };

      return valData;
    }

    // DATA
    @Prop() public payloadData!: {
      orderId: string;
      customerId: string;
      partnerId: string;
    };

    public data: {[key: string]: any} = {
      importFile: null,
    };

    public errors: Array<{
      code: string,
      message: string,
    }> = [];

    public agreementOptions: Array<{ value: string, label: string }> = [];
    public selectedAgreementId: string | null = null;

    // METHODS
    public close() {
      deanDialogBus.$emit('close');
    }

    public currentlySelectedAgreement() {
      if (this.selectedAgreementId !== null) {
        const found = this.agreementOptions.find((o) => o.value === this.selectedAgreementId);
        return found || null;
      }
      return null;
    }

    public save() {
      // @ts-ignore-next-line
      this.$v.data.$touch();
      // @ts-ignore-next-line
      this.$v.selectedAgreementId.$touch();

      if (!this.$v.$invalid) {

        this.$emit('setLoad', true);
        this.errors = [];

        if (this.agreementOptions.length === 1) {
          this.doImport(this.agreementOptions[0].value);
        } else {
          this.doImport(this.selectedAgreementId!);
        }
      } else {
        this.$toast(createNotification(this.$v));
      }
    }

    public doImport(partnerAgreementId: string) {
      this.$emit('setLoad', true);

      const formData: FormData = new FormData();
      formData.append('orderId', this.payloadData.orderId.toString());
      formData.append('partnerAgreementId', partnerAgreementId);
      formData.append('file', this.data.importFile);

      axios.post('/-/trade/api/order.products.import', formData).then(
        (response) => {
          deanDialogBus.$emit('saved');
          this.$toast(this.$store.state.main.notification.messages.dataSaved);
          this.close();
        },
        (error) => {
          const errorResponse = error.response.data;

          if (errorResponse.errors?.message) {
            this.$toast({
              icon: warningIcon,
              title: 'Waarschuwing',
              text: errorResponse.errors.message,
              theme: 'warning',
            });
          } else if (errorResponse.errors && errorResponse.errors.length > 0) {
            error.response.data.errors.forEach((error: any) => {
              this.errors.push({
                code: error.code,
                message: error.message,
              });
            });
          } else {
            this.$toast(this.$store.state.main.notification.messages.couldNotSaveData);
          }
        },
      ).finally(() => {
        this.$emit('setLoad', false);
      });
    }

    public importFileChanged(uploadFileData: any) {
      this.data.importFile = uploadFileData.get('file');
    }

    public created() {
      this.$emit('setLoad', false);

      axios.post('/-/trade/api/customer.partner.agreements.list', {
        id: this.payloadData.partnerId,
        activeOnly: true,
      }).then(
        (response) => {
          const agreements = response.data.data;
          if (agreements.length === 0) {
            this.$toast({
              title: 'Fout',
              text: 'De partner van deze klant heeft geen overeenkomsten, importeren is niet mogelijk.',
              theme: 'danger',
            });
          } else {
            this.agreementOptions = agreements.map((agreement: any) => ({
              value: agreement.id,
              label: agreement.description + ' (' + agreement.typeName + ')',
            }));
          }
        },
        (error) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      );
    }
  }
</script>

<!-- STYLE -->
<style lang="scss" scoped>

.dialog :deep(.dean-dialog-container) {
  width: 600px;
}

:deep(.dean-input-file .input-container) {
  padding-top: 6px;
}

:deep(.dean-input-file label) {
  display: none !important;
}

.input-label {
  padding-left: 20px;
}

.dean-form-row-errors {
  margin-top: 15px;
  margin-left: 15px;
  margin-right: 15px;
}

</style>
